import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import RadioButton from 'components/atoms/radioButton';
import { Field, Formik, Form } from 'formik';
import { showResult } from 'infrastructure/hooks/utils';
import Input from 'components/atoms/input';
import BaseSelectBox from 'components/atoms/base-select-box';
import useUser from 'utils/useUser';
import * as Yup from 'yup';
import { format, parse } from 'date-fns';
import common from 'constants/common';
import { parseDateSafe } from 'utils/timeHelper';
import { loadDoctorsAndAgencyDoctorByRoles } from 'utils/loadDoctorsAndAgencyDoctors';
import {
  completionAppointmentMethod,
  nextAppointmentMethod,
} from 'constants/forms';
import MasterModal from 'components/organisms/masterModal';
import { getPatientInfoKey } from 'infrastructure/hooks/patient/use-patient-info';
import { useQueryClient } from '@tanstack/react-query';
import { getNextAppointmentKey } from 'infrastructure/hooks/patient/use-patient-next-appointment';
import { RoleGroups } from 'infrastructure/consts/role-groups';

import s from './style.module.scss';

const schema = Yup.object().shape({
  completedByAgencyDoctorGuid: Yup.string().required('required'),
  completedDate: Yup.string()
    .required('required')
    .test(
      'date-format-is-valid',
      `Date format must be ${common.dateFormats.priorAuthorizationAdmin}`,
      (value) => {
        return parseDateSafe(
          parse(value, common.dateFormats.priorAuthorizationAdmin, new Date()),
        );
      },
    ),
  appointmentMethod: Yup.string().required('required'),
  nextAppointmentScheduled: Yup.bool().required('required'),
  scheduledWithAgencyDoctorGuid: Yup.string().when('nextAppointmentScheduled', {
    is: true,
    then: Yup.string().required('required'),
  }),
  scheduledDate: Yup.string().when('nextAppointmentScheduled', {
    is: true,
    then: Yup.string()
      .required('required')
      .test(
        'date-format-is-valid',
        `Date format must be ${common.dateFormats.full}`,
        (value) => {
          return parseDateSafe(
            parse(value, common.dateFormats.full, new Date()),
          );
        },
      ),
  }),
  nextAppointmentMethod: Yup.string().when('nextAppointmentScheduled', {
    is: true,
    then: Yup.string().required('required'),
  }),
});

const CcmInteractionStatusModal = ({
  toggle,
  isOpen,
  patientGuid,
  onLaterSend,
}) => {
  const queryClient = useQueryClient();
  const patientInfoKey = getPatientInfoKey(patientGuid);
  const nextAppointmentKey = getNextAppointmentKey(patientGuid);

  const user = useUser();
  const { togglePatientCcmInteractionStatus: updateLoading } = useSelector(
    (state) => state.loading.effects.adminPanelPatients,
  );

  const {
    adminPanelPatients: { togglePatientCcmInteractionStatus },
  } = useDispatch();

  const [completedByOptions, setCompletedByOptions] = useState([]);
  const [completedByOptionsLoading, setCompletedByOptionsLoading] =
    useState(false);

  const [nextAppointmentOptions, setNextAppointmentOptions] = useState([]);
  const [nextAppointmentOptionsLoading, setNextAppointmentOptionsLoading] =
    useState(false);

  const [initialValues, setInitialValues] = useState({
    completedByAgencyDoctorGuid: undefined,
    scheduledWithAgencyDoctorGuid: undefined,
    completedDate: format(
      new Date(),
      common.dateFormats.priorAuthorizationAdmin,
    ),
  });

  const submit = async (values) => {
    const completedByType = completedByOptions.find(
      (e) => e.guid === values.completedByAgencyDoctorGuid,
    )?.type;

    let completedByData = {
      completedByAgencyDoctorGuid: values.completedByAgencyDoctorGuid,
    };

    if (completedByType === 'doctor')
      completedByData = {
        completedByDoctorGuid: values.completedByAgencyDoctorGuid,
      };

    const postData = {
      isInteracted: true,
      appointmentCompletion: {
        completedDate: values.completedDate,
        appointmentMethod: values.appointmentMethod,
        ...completedByData,
      },
    };
    if (values.nextAppointmentScheduled) {
      const scheduledWithType = nextAppointmentOptions.find(
        (e) => e.guid === values.scheduledWithAgencyDoctorGuid,
      )?.type;

      let scheduledWithData = {
        scheduledWithAgencyDoctorGuid: values.scheduledWithAgencyDoctorGuid,
      };

      if (scheduledWithType === 'doctor')
        scheduledWithData = {
          scheduledWithDoctorGuid: values.scheduledWithAgencyDoctorGuid,
        };

      postData.nextAppointment = {
        scheduledDate: values.scheduledDate,
        appointmentMethod: values.nextAppointmentMethod,
        ...scheduledWithData,
      };
    }
    const { error, data } = await togglePatientCcmInteractionStatus({
      patientGuid,
      postData,
    });

    if (error) {
      showResult(error);
      return;
    }

    if (data) {
      showResult();
      if (typeof window.loadNextAppointment === 'function') {
        window.loadNextAppointment();
      }
      if (typeof window.loadAppointmentCompletions === 'function') {
        window.loadAppointmentCompletions();
      }
      queryClient.setQueryData(patientInfoKey, (oldData) => {
        if (oldData && data) {
          return {
            ...oldData,
            ccmInteractionStatus: data.ccmInteractionStatus,
          };
        }
      });
      queryClient.invalidateQueries(patientInfoKey);
      queryClient.invalidateQueries(nextAppointmentKey);
    }
    toggle(false);
  };

  const handleSubmit = useCallback(
    (values) => {
      if (onLaterSend) onLaterSend(() => submit(values));
      else submit(values);
    },
    [onLaterSend, submit],
  );

  useEffect(() => {
    if (!isOpen) return;

    if (
      completedByOptions.length === 0 &&
      completedByOptionsLoading === false
    ) {
      loadDoctorsAndAgencyDoctorByRoles({
        patientGuid,
        roles: RoleGroups.AppointmentCompletedByAllowedRoles,
        setData: setCompletedByOptions,
        setLoading: setCompletedByOptionsLoading,
      });
    }

    if (
      nextAppointmentOptions.length === 0 &&
      nextAppointmentOptionsLoading === false
    ) {
      loadDoctorsAndAgencyDoctorByRoles({
        patientGuid,
        roles: RoleGroups.CCMRoles.join(','),
        setData: setNextAppointmentOptions,
        setLoading: setNextAppointmentOptionsLoading,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    const completedByAgencyDoctorGuid = completedByOptions.find(
      ({ id }) => id === user.guid,
    )?.id;
    const scheduledWithAgencyDoctorGuid = nextAppointmentOptions.find(
      ({ id }) => id === user.guid,
    )?.id;
    setInitialValues({
      ...initialValues,
      completedByAgencyDoctorGuid,
      scheduledWithAgencyDoctorGuid,
    });
  }, [completedByOptions, nextAppointmentOptions]);

  const formRef = useRef();

  useEffect(() => {
    formRef.current?.resetForm();
    formRef.current?.setFieldValue('completedByAgencyDoctorGuid', '');
    formRef.current?.setFieldValue('scheduledWithAgencyDoctorGuid', '');
  }, [patientGuid]);

  if (!isOpen) return null;
  return (
    <MasterModal
      isOpen={isOpen}
      toggle={() => toggle(false)}
      title="Appointment Completion"
      size="large"
      saveLabel="Submit"
      isSaveLoading={updateLoading}
      onSave={() => formRef.current?.submitForm()}
      bodyClassName={s['modal-body']}
    >
      <>
        <Formik
          innerRef={formRef}
          initialValues={initialValues}
          enableReinitialize
          onSubmit={handleSubmit}
          validationSchema={schema}
        >
          {({ values: { nextAppointmentScheduled }, errors }) => (
            <Form>
              <>
                <Field name="completedByAgencyDoctorGuid">
                  {({ field, form }) => (
                    <div className={s['select-field']}>
                      <label className={s['select-label']}>
                        Appointment Completed By
                      </label>
                      <BaseSelectBox
                        data-cy="completedByAgencyDoctorGuid"
                        options={completedByOptions}
                        loading={completedByOptionsLoading}
                        value={field.value || null}
                        invalid={!!errors.completedByAgencyDoctorGuid}
                        onChange={(val) =>
                          form.setFieldValue('completedByAgencyDoctorGuid', val)
                        }
                      />
                      {errors.completedByAgencyDoctorGuid && (
                        <div className="input-error">
                          {errors.completedByAgencyDoctorGuid}
                        </div>
                      )}
                    </div>
                  )}
                </Field>
              </>
              <>
                <Input
                  label="Date and Time of appointment"
                  id="completedDate"
                  type="date"
                  format={common.dateFormats.priorAuthorizationAdmin}
                  invalid={!!errors.completedDate}
                />
                {errors.completedDate && (
                  <div className="input-error">{errors.completedDate}</div>
                )}
              </>
              <>
                <Field name="appointmentMethod">
                  {({ field, form }) => (
                    <div className={s['select-field']}>
                      <label className={s['select-label']}>
                        Appointment Completion Method
                      </label>
                      <BaseSelectBox
                        data-cy="appointmentMethod"
                        options={completionAppointmentMethod}
                        value={field.value || null}
                        invalid={!!errors.appointmentMethod}
                        onChange={(val) =>
                          form.setFieldValue('appointmentMethod', val)
                        }
                      />
                      {errors.appointmentMethod && (
                        <div className="input-error">
                          {errors.appointmentMethod}
                        </div>
                      )}
                    </div>
                  )}
                </Field>
              </>
              <div className={s['next-appointment-section']}>
                <div className={s['section-title']}>Next Appointment</div>
                <div>
                  <div className={s['radio-group-label']}>
                    Is Next Appointment Scheduled
                  </div>
                  <div className={s['radio-group']}>
                    <Field name="nextAppointmentScheduled">
                      {({ field, form }) => (
                        <>
                          <RadioButton
                            key="nextAppointmentScheduledYes"
                            name="nextAppointmentScheduledYes"
                            id="nextAppointmentScheduledYes"
                            label="Yes"
                            onChange={() => {
                              form.setFieldValue(
                                'nextAppointmentScheduled',
                                true,
                              );
                            }}
                            isChecked={field.value === true}
                          />
                        </>
                      )}
                    </Field>
                    <Field name="nextAppointmentScheduled">
                      {({ field, form }) => (
                        <RadioButton
                          key="nextAppointmentScheduledNo"
                          name="nextAppointmentScheduledNo"
                          id="nextAppointmentScheduledNo"
                          label="No"
                          onChange={() => {
                            form.setFieldValue(
                              'nextAppointmentScheduled',
                              false,
                            );
                          }}
                          isChecked={field.value === false}
                        />
                      )}
                    </Field>
                  </div>
                  {errors.nextAppointmentScheduled && (
                    <div className="input-error">
                      {errors.nextAppointmentScheduled}
                    </div>
                  )}
                  {nextAppointmentScheduled && (
                    <div className={s['sub-fields']}>
                      <Field name="scheduledWithAgencyDoctorGuid">
                        {({ field, form }) => (
                          <div className={s['select-field']}>
                            <label className={s['select-label']}>
                              Who is the next appointment scheduled with
                            </label>
                            <BaseSelectBox
                              data-cy="scheduledWithAgencyDoctorGuid"
                              options={nextAppointmentOptions}
                              loading={nextAppointmentOptionsLoading}
                              value={field.value || null}
                              invalid={!!errors.scheduledWithAgencyDoctorGuid}
                              onChange={(val) =>
                                form.setFieldValue(
                                  'scheduledWithAgencyDoctorGuid',
                                  val,
                                )
                              }
                            />
                            {errors.scheduledWithAgencyDoctorGuid && (
                              <div className="input-error">
                                {errors.scheduledWithAgencyDoctorGuid}
                              </div>
                            )}
                          </div>
                        )}
                      </Field>
                      <Input
                        label="Date and Time of the next appointment (CST Time Zone)"
                        id="scheduledDate"
                        type="dateWithTime"
                        timeCaption="CST Time Zone"
                        invalid={!!errors.scheduledDate}
                      />
                      {errors.scheduledDate && (
                        <div className="input-error">
                          {errors.scheduledDate}
                        </div>
                      )}
                      <Field name="nextAppointmentMethod">
                        {({ field, form }) => (
                          <div className={s['select-field']}>
                            <label className={s['select-label']}>
                              Next Month Appointment Method
                            </label>
                            <BaseSelectBox
                              data-cy="nextAppointmentMethod"
                              options={nextAppointmentMethod}
                              value={field.value || null}
                              invalid={!!errors.nextAppointmentMethod}
                              onChange={(val) =>
                                form.setFieldValue('nextAppointmentMethod', val)
                              }
                            />
                            {errors.nextAppointmentMethod && (
                              <div className="input-error">
                                {errors.nextAppointmentMethod}
                              </div>
                            )}
                          </div>
                        )}
                      </Field>
                    </div>
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </>
    </MasterModal>
  );
};

CcmInteractionStatusModal.propTypes = {
  toggle: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  patientGuid: PropTypes.string,
  onLaterSend: PropTypes.func,
};

export default CcmInteractionStatusModal;
